import React, { useState, useCallback, useRef, useEffect, useMemo } from "react"; 
import axios from "axios";
import { REPORTS_API_ROOT } from "./config/api";
import "bootstrap/dist/css/bootstrap.min.css";
import { useNavigate } from "react-router-dom";

export default function PurchaserReport({ user }) {
  const role = user?.role?.toLowerCase();
  const isViewer = user?.role === "viewer";
  const isPurchaser = user?.role === "purchaser";
  const cancelTokenRef = useRef(null);

  const navigate = useNavigate();

const handleLogout = () => {
  localStorage.removeItem("currentUser");  // remove login session
  localStorage.removeItem("user");         // if stored anywhere else
  localStorage.clear();                    // safest
  navigate("/");                           // redirect to login page
  window.location.reload();                // force refresh
};


  // -------------------- States --------------------
  const [purchaser, setPurchaser] = useState(isViewer ? user.username : "");
  const [purchasers, setPurchasers] = useState(isViewer ? [user.username] : []);
  const [originalData, setOriginalData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const tableContainerRef = useRef(null);
  const today = new Date();
  const [fromDate, setFromDate] = useState(today.toISOString().split("T")[0]);
  const [toDate, setToDate] = useState(today.toISOString().split("T")[0]);
  const [columnFilters, setColumnFilters] = useState({});
const [appliedFromDate, setAppliedFromDate] = useState(fromDate);
const [appliedToDate, setAppliedToDate] = useState(toDate);
const [initialLoadDone, setInitialLoadDone] = useState(false);


  // -------------------- Column mapping --------------------
  const columnMap = {
    EMPMST_NAME: "PUR NAME",
    STK_GRP2_DESC: "ITEM",
    PUR_AVG: "PUR AVG",
    PURDET_CLAIM_QTY: "Claim KG",
    DamageKG: "DMG KG",
    FOC_KG: "FOC KG",
    DamageValueAED: "DMG AED",
    FOC_AmountAED: "FOC AED",
  };

  // -------------------- Helper functions --------------------
  const normalizeRows = useCallback((rows) => {
    if (!Array.isArray(rows)) return [];
    return rows.map((r) => ({
      ...r,
      EMPMST_NAME: r?.EMPMST_NAME || r?.purchaserName || "",
      ITEM_NAME: r?.ITEM_NAME || r?.itemName || "",
      QTY: r?.QTY || 0,
      PURDET_CLAIM_QTY: r?.PURDET_CLAIM_QTY || 0,
    }));
  }, []);

  const logoutBtnStyle = {
  backgroundColor: "#dc3545",
  color: "#fff",
  border: "none",
  borderRadius: "8px",
  padding: "6px 14px",
  cursor: "pointer",
  fontWeight: "bold",
};


  const normalizeName = (name) => name?.toLowerCase().replace(/\s/g, "");

useEffect(() => {
  if (!Array.isArray(originalData) || originalData.length === 0) return;

  let filtered = [...originalData];

  if (purchaser && purchaser !== "All Purchasers") {
    filtered = filtered.filter(
      (row) => normalizeName(row?.EMPMST_NAME) === normalizeName(purchaser)
    );
  }

  Object.entries(columnFilters ?? {}).forEach(([col, value]) => {
    if (value) {
      filtered = filtered.filter(row =>
        String(row?.[col] ?? "").toLowerCase().includes(value.toLowerCase())
      );
    }
  });

  setFilteredData(filtered);
}, [purchaser, originalData, columnFilters]);

// -------------------- Proceed Button --------------------
const handleProceed = useCallback(() => {
  if (!fromDate || !toDate) return;

  if (cancelTokenRef.current) {
    cancelTokenRef.current.cancel("New request");
  }
  cancelTokenRef.current = axios.CancelToken.source();

  const API_BASE = REPORTS_API_ROOT;

  setLoading(true);
  setError("");
  setFilteredData([]);
  setOriginalData([]);

  axios
    .get(`${API_BASE}/api/purchases`, {
      params: { fromDate, toDate },
      cancelToken: cancelTokenRef.current.token,
      timeout: 120000,
    })
    .then((res) => {
      const fetchedData = Array.isArray(res.data)
        ? res.data
        : res.data?.data || [];

      const data = normalizeRows(fetchedData).filter(r => r?.EMPMST_NAME);
      setOriginalData(data);

      const apiPurchasers = [
        "All Purchasers",
        ...[...new Set(data.map(r => r?.EMPMST_NAME))].sort(),
      ];
      setPurchasers(apiPurchasers);

      if (user?.username) {
        const normalizedUser = normalizeName(user.username);
        const matched =
          apiPurchasers.find(p => normalizeName(p) === normalizedUser) ||
          apiPurchasers.find(p => normalizeName(p).startsWith(normalizedUser)) ||
          "All Purchasers";
        setPurchaser(matched);
      } else {
        setPurchaser("All Purchasers");
      }

      setAppliedFromDate(fromDate);
      setAppliedToDate(toDate);

      // ✅ initial load complete, unfreeze inputs
      setInitialLoadDone(true);
    })
    .catch((err) => {
      if (!axios.isCancel(err)) {
        setError("Failed to fetch data");
      }
    })
    .finally(() => setLoading(false));

  setColumnFilters({});
}, [fromDate, toDate, user, normalizeRows]);

useEffect(() => {
  handleProceed();
}, []);
  // -------------------- Totals --------------------
  const totals = useMemo(() => {
    if (!Array.isArray(filteredData) || filteredData.length === 0) return {};

    const totalsObj = {};
    totalsObj.STK_GRP2_DESC = `Items: ${new Set(filteredData.map((r) => r?.STK_GRP2_DESC)).size}`;

    const numericCols = [
      "PUR_AVG",
      "DamageKG",
      "PURDET_CLAIM_QTY",
      "FOC_KG",
      "DamageValueAED",
      "FOC_AmountAED",
    ];

    numericCols.forEach((key) => {
      const sum = filteredData.reduce((acc, r) => acc + (parseFloat(r?.[key]) || 0), 0);
      totalsObj[key] = sum
        ? sum.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
        : "-";
    });

    return totalsObj;
  }, [filteredData]);

  // -------------------- Styles --------------------
  const inputStyle = { padding: "6px 8px", borderRadius: "8px", border: "1px solid #ccc", fontSize: "13px" };
  const purchaserDropdownStyle = { ...inputStyle };
  const thStyle = { backgroundColor: "#198754", color: "white", padding: "8px", textAlign: "center"};
  const tdStyle = { padding: "8px", borderBottom: "1px solid #eee" };

  // -------------------- Render --------------------
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100vh", backgroundColor: "#fff", borderRadius: "12px", boxShadow: "0 2px 10px rgba(0,0,0,0.08)", padding: "10px" }}>
      {/* Filter Section */}
      <div
  className="filter-bar"
  style={{
    position: "sticky",
    top: 0,
    zIndex: 50,
    background: "#fff",
    paddingBottom: "8px"
  }}
>
        <div className="filter-row unified-filter">
          <input
  type="date"
  value={fromDate}
  onChange={e => setFromDate(e.target.value)}
  style={inputStyle}
  disabled={!initialLoadDone}  // ✅ disabled until today's data loads
/>
<input
  type="date"
  value={toDate}
  onChange={e => setToDate(e.target.value)}
  style={inputStyle}
  disabled={!initialLoadDone}
/>
<select
  value={purchaser}
  onChange={e => setPurchaser(e.target.value)}
  style={purchaserDropdownStyle}
  disabled={!initialLoadDone}
>
  {(Array.isArray(purchasers) ? purchasers : []).map((name, idx) => (
    <option key={idx} value={name}>{name}</option>
  ))}
</select>
<button
  onClick={handleProceed}
  style={{
    padding: "6px 16px",
    background: "#198754",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    fontSize: "13px",
    cursor: initialLoadDone ? "pointer" : "not-allowed",
    whiteSpace: "nowrap"
  }}
  disabled={!initialLoadDone || loading} // freeze until initial load
>
  Proceed
</button>
<button onClick={handleLogout} style={logoutBtnStyle}>
    ⏻ Logout
  </button>

        </div>
      </div>
       <div className="form-header">
  <div className="date-footer">
    Showing data from&nbsp;
    <strong>{appliedFromDate}</strong>
    &nbsp;→&nbsp;
    <strong>{appliedToDate}</strong>
  </div>
</div>
      {/* Table Section */}
      <div ref={tableContainerRef} className="table-container" style={{ flexGrow: 1, overflowY: "auto", position: "relative", height: "calc(100vh - 210px)", width: "100%", margin: 0, padding: 0 }}>
        <div className="table-scroll-wrapper">
          {!loading && Array.isArray(filteredData) && filteredData.length > 0 && (
            <table className="report-table">
              <thead>
  <tr>
    {Object.keys(columnMap ?? {}).map(col => <th key={col} style={thStyle}>{columnMap[col]}</th>)}
  </tr>
  <tr>
    {Object.keys(columnMap ?? {}).map(col => (
      <th key={col}
        style={{ textAlign: "center" }}>
        <input
          type="text"
          placeholder="search"
          value={columnFilters?.[col] ?? ""}
          onChange={e => setColumnFilters({ ...columnFilters, [col]: e.target.value })}
          style={{
            width: "100px",       // smaller width
            padding: "2px 4px",  // less padding
            fontSize: "12px",     // smaller font
            borderRadius: "3px",
            border: "1px solid #ccc"
          }}
        />
      </th>
    ))}
  </tr>
</thead>

              <tbody>
                {(Array.isArray(filteredData) ? filteredData : []).map((row, idx) => {
                  if (!row) return null;
                  const numericCols = ["PUR_AVG","DamageKG","PURDET_CLAIM_QTY","FOC_KG","DamageValueAED","FOC_AmountAED"];
                  return (
                    <tr key={idx} style={{ backgroundColor: idx % 2 ? "#f0fdf4" : "#fff" }}>
                      {Object.keys(columnMap ?? {}).map((col, i) => {
                        let cellValue = row?.[col] ?? "-";
                        if (numericCols.includes(col) && row?.[col] !== undefined && row?.[col] !== null) {
                          cellValue = Number(row[col]).toFixed(2);
                        }
                        return <td key={col} style={{ ...tdStyle, textAlign: "left" }}>{cellValue}</td>;
                      })}
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr>
                  {Object.keys(columnMap ?? {}).map((col, i) => (
                    <td key={col} style={{ padding: "8px", fontWeight: "bold" }}>
                      {i === 0 ? "Total:" : totals?.[col] ?? "-"}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          )}
        </div>
      </div>

      {/* Loading */}
      {loading && (
        <div style={{ display: "flex", justifyContent: "center", alignItems: "center", flexDirection: "column", gap: "20px", width: "100%", height: "100vh" }}>
          <div style={{ display: "flex", gap: "13px" }}>
            {["🍎", "🥦", "🥩", "🫐", "🍌"].map((item, idx) => <div key={idx} style={{ fontSize: "40px", animation: `bounce 1s ease-in-out ${idx * 0.1}s infinite` }}>{item}</div>)}
          </div>
          <div style={{ fontSize: "24px", fontWeight: "bold" }}>Loading...</div>
          <style>{`@keyframes bounce {0%,100%{transform:translateY(0);}50%{transform:translateY(-20px);}}`}</style>
        </div>
      )}

      {/* --- Styles --- */}
     <style>{`
  /* ---------------- Base Table Styles ---------------- */
.report-table { 
  width: 100%; 
  border-collapse: collapse; 
  table-layout: fixed; 
  font-size: 13px; 
}
.report-table th, .report-table td { 
  padding: 8px 6px;   /* slightly reduced for compact look */
  border: 1px solid #d1fae5; 
  text-align: left; 
  word-wrap: break-word;
  height: auto;       /* ensure rows are not tall */
}

.report-table tfoot { 
  position: sticky; 
  bottom: 0; 
  background-color: #dcfce7; 
  z-index: 5; 
}
.report-table tfoot td { 
  border-top: 2px solid #86efac; 
  font-weight: bold; 
}

.form-header {
  display: flex;
  justify-content: flex-start;
  margin-bottom: 8px;
}

.date-footer {
  position: sticky;
  bottom: 0;
  margin-top: 8px;
  padding: 6px 10px;
  font-size: 13px;
  color: #555;
  text-align: right;
  background: #ffffff;           /* important */
  border-top: 1px solid #e5e7eb;
  z-index: 5;
}


/* ---------------- Filter Bar Layout ---------------- */
.filter-bar {
  display: flex;
  flex-direction: column;
  gap: 10px;
  margin-bottom: 10px;
}
.unified-filter {
  display: flex;
  flex-direction: row;
  gap: 15px;
  flex-wrap: wrap;
}
.unified-filter input,
.unified-filter select,
.unified-filter button {
  min-width: 160px;
  padding: 6px 8px;
  border-radius: 8px;
  border: 1px solid #ccc;
  font-size: 13px;
}

/* ---------------- Table Search Input ---------------- */
.report-table thead input {
  width: 100%;
  padding: 3px 4px;
  font-size: 10px;
  border-radius: 3px;
  border: 1px solid #ccc;
}

/* ===== Correct sticky stacking ===== */

/* Header row (labels) */
.report-table thead tr:first-child th {
  position: sticky;
  top: 2px; /* below filter bar */
  background: #198754;
  color: #fff;
  z-index: 20;
}

/* Search row */
.report-table thead tr:nth-child(2) th {
  position: sticky;
  top: 32px; /* filter bar + header row */
  background: #fff;
  z-index: 19;
}

/* ---------------- Mobile View (<=768px) ---------------- */

@media (max-width: 768px) {
  /* Filter bar compact layout */
  .filter-bar {
    flex-direction: column;
    gap: 8px;
  }

  /* Line 1: dates side-by-side */
  .unified-filter:first-child {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 8px;
  }

  /* Line 2: dropdown + logout */
  .unified-filter:nth-child(2) {
    display: grid;
    grid-template-columns: 1fr auto;
    gap: 8px;
    align-items: center;
  }

  /* Compact date and dropdowns */
  .unified-filter input[type="date"],
  .unified-filter select {
    font-size: 10px;
    padding: 4px 5px;
    min-width: 100px;
    border-radius: 5px;
  }

  /* Smaller logout button */
  .unified-filter button {
    font-size: 11px;
    padding: 4px 8px;
    border-radius: 5px;
  }

  /* Compact table */
  @media (max-width: 768px) {
  /* Table container for horizontal scroll */
  .table-container {
    overflow-x: auto;
    -webkit-overflow-scrolling: touch;
    position: relative;
  }

  .report-table {
    font-size: 9px;
    min-width: 600px;
    border-collapse: collapse;
    table-layout: fixed;
  }

  .report-table th,
  .report-table td {
    padding: 4px 3px;
    font-size: 8px;
    height: auto;
  }


  .report-table thead input {
    width: 50px;
    font-size: 8px;
    padding: 2px 3px;
  }

  .report-table tfoot td {
    padding: 4px 3px;
    font-weight: bold;
    position: sticky;
    bottom: 0;
    background-color: #dcfce7;
    z-index: 5;
  }
    @media (max-width: 768px) {
    .main-content {
    margin-left: 0 !important;
    padding: 12px !important;
    width: 100% !important;
  }
    /* TABLE AREA (ONLY SCROLL AREA) */
  .popup-table-wrapper {
    flex: 1;                  /* 🔑 fills remaining space */
    overflow-y: auto;
    overflow-x: auto;
    background: #ffffff;
  }

  /* TABLE */
  .popup-table-wrapper table {
    width: 100%;
    font-size: 13px;
    border-collapse: collapse;
  }

  .popup-table-wrapper thead {
    position: sticky;
    top: 0;
    background: #198754;
    color: #fff;
    z-index: 5;
  }

  .popup-table-wrapper tfoot {
    position: sticky;
    bottom: 0;
    background: #e9f5ee;
    font-weight: 600;
    z-index: 5;
  }

}

@media (max-width: 768px) {
  .main-content,
  .container,
  .table-wrapper {
    transform: none !important;
  }
}


`}</style>

    </div>
  );
}
